import { useEffect, useMemo, useRef, useState, type FormEvent } from "react"
import { useTranslation } from "react-i18next"
import {
  faArrowUpRightFromSquare,
  faCircleQuestion,
  faClose,
  faEraser,
  faMessage,
  faPaperPlane,
  faSearch,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ERequestType } from "../../Enum/ERequestType"
import type { OperationalHelpAction } from "../../Objects/OperationalHelp/OperationalHelpAction"
import type { EOperationalHelpSurface } from "../../Objects/OperationalHelp/EOperationalHelpSurface"
import { useOperationalHelp } from "../../hooks/OperationalHelp/useOperationalHelp"
import { FloatButton } from "../FloatButton/FloatButton"
import ModalPortal from "../Modal/ModalPortal"
import Animate, { AnimationTypes } from "../Animate/Animate"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../Titles/Titles"
import THEME from "../../configuration/Theme"
import { ButtonIcon } from "../Button/Button"
import { SIZE } from "../../configuration/Sizes"
import InputField from "../InputField/InputField"
import { getQuickSearchActionTypeConfig } from "../../utils/quickSearchActionTypeConfig"
import { useMenuMobileContext } from "../../context/useMenuMobileContext"
import GetSpace from "../../utils/space"

type OperationalHelpFloatingButtonProps = {
  surface: EOperationalHelpSurface
  requestType: ERequestType
  availableActions: OperationalHelpAction[]
  onAction: (action: OperationalHelpAction) => void
  userName?: string
  bottom?: number | string
  right?: number | string
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
  onOpenQuickSearch?: () => void
}

type OperationalHelpPopupProps = OperationalHelpFloatingButtonProps & {
  onClose: () => void
}

type OperationalHelpMessage = {
  id: number
  role: "assistant" | "user"
  text: string
  actionIds?: string[]
  isError?: boolean
}

export function OperationalHelpFloatingButton(props: OperationalHelpFloatingButtonProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false)
  
  const { isNotMobile } = useMenuMobileContext()
  
  const isOpen = props.isOpen ?? internalIsOpen
  
  const setIsOpen = props.onOpenChange ?? setInternalIsOpen

  return (
    <>
      <FloatButton
        icon={faMessage}
        onClick={() => setIsOpen(true)}
        gradient="linear-gradient(to right, #06b6d4, #2563eb)"
        iconColor="#ffffff"
        size={isNotMobile ? 85 : 50}
        iconSize={isNotMobile ? "3x" : "2x"}
        bottom={props.bottom}
        right={props.right}
        descriptionKey="interactionDescriptions:components.operationalHelp.floatButton.open"
      />

      {isOpen && <OperationalHelpPopup {...props} onClose={() => setIsOpen(false)} />}
    </>
  )
}

function OperationalHelpPopup({
  surface,
  requestType,
  availableActions,
  onAction,
  userName,
  onClose,
  onOpenQuickSearch,
}: OperationalHelpPopupProps) {
  const { t } = useTranslation("operationalHelp")

  const { askOperationalHelp } = useOperationalHelp()

  const buildInitialMessage = (): OperationalHelpMessage => ({
    id: Date.now(),
    role: "assistant",
    text: t("intro", { name: userName?.trim() || t("default_name") }),
  })

  const [question, setQuestion] = useState("")

  const [messages, setMessages] = useState<OperationalHelpMessage[]>(() => [buildInitialMessage()])

  const [isLoading, setIsLoading] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement | null>(null)

  const actionById = useMemo(() => new Map(availableActions.map((action) => [action.id, action])), [availableActions])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
  }, [messages, isLoading])

  const resolveActions = (actionIds?: string[]) =>
    actionIds?.map((id) => actionById.get(id)).filter(Boolean) as OperationalHelpAction[] | undefined

  const handleAsk = async () => {
    const trimmedQuestion = question.trim()

    if (!trimmedQuestion || isLoading) {
      return
    }

    setIsLoading(true)
    setQuestion("")
    setMessages((current) => [
      ...current,
      {
        id: Date.now(),
        role: "user",
        text: trimmedQuestion,
      },
    ])

    try {
      const nextResponse = await askOperationalHelp(
        {
          question: trimmedQuestion,
          surface,
          availableActions,
        },
        requestType,
      )

      setMessages((current) => [
        ...current,
        {
          id: Date.now() + 1,
          role: "assistant",
          text: nextResponse.reply,
          actionIds: nextResponse.actionIds,
        },
      ])
    } catch {
      setMessages((current) => [
        ...current,
        {
          id: Date.now() + 1,
          role: "assistant",
          text: t("error"),
          isError: true,
        },
      ])
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    void handleAsk()
  }

  const handleClearChat = () => {
    setMessages([buildInitialMessage()])
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={`${defaultStyle.alert2} flex h-[82dvh] max-h-[82dvh] flex-col overflow-hidden`}>
            <TitlePageResponsiveIconSmall
              title={t("title")}
              icon={faCircleQuestion}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  {onOpenQuickSearch && (
                    <ButtonIcon
                      sizeNumber={SIZE.buttonCloseIconSize}
                      bgColor="#2563eb"
                      iconColor="#ffffff"
                      icon={faSearch}
                      onClick={onOpenQuickSearch}
                      size="small"
                      descriptionKey="interactionDescriptions:components.operationalHelp.popup.openQuickSearch"
                    />
                  )}

                  <GetSpace left={5} />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:components.operationalHelp.popup.close"
                  />
                </div>
              }
            />

            <div className={defaultStyle.divisionBar}></div>

            <section className="min-h-0 w-full flex-1 overflow-y-auto p-2">
              <div className="w-full space-y-3">
                {messages.map((message) => {
                  const messageActions = resolveActions(message.actionIds)
                  const isUser = message.role === "user"

                  return (
                    <div key={message.id} className="w-full space-y-3">
                      <div className={`flex w-full ${isUser ? "justify-end" : "justify-start"}`}>
                        <div className="max-w-[92%] sm:max-w-[82%]">
                          <p
                            className={`whitespace-pre-line rounded-2xl px-3 py-2 text-sm font-semibold leading-relaxed shadow-sm ${
                              isUser
                                ? "rounded-br-md bg-orange-500 text-white"
                                : message.isError
                                  ? "rounded-bl-md border border-red-200 bg-red-50 text-red-700"
                                  : "rounded-bl-md border border-slate-200 bg-slate-50 text-slate-700"
                            }`}
                          >
                            {message.text}
                          </p>
                        </div>
                      </div>

                      {messageActions && messageActions.length > 0 && (
                        <div className="w-full space-y-2">
                          <div className={defaultStyle.divisionBar}></div>

                          <div className="space-y-2">
                            <h3 className="text-xs font-black uppercase text-slate-600">{t("actions_title")}</h3>

                            <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
                              {messageActions.map((action) =>
                                (() => {
                                  const typeConfig = getQuickSearchActionTypeConfig(action.type)

                                  return (
                                    <button
                                      key={action.id}
                                      type="button"
                                      onClick={() => {
                                        onAction(action)
                                        onClose()
                                      }}
                                      className="flex min-h-14 w-full items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm font-black text-slate-800 shadow-sm transition hover:border-slate-300 hover:shadow-md"
                                      data-interaction-description-key="interactionDescriptions:components.operationalHelp.popup.action"
                                    >
                                      <span
                                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg shadow-sm ${typeConfig.iconClassName}`}
                                      >
                                        <FontAwesomeIcon icon={typeConfig.icon} />
                                      </span>

                                      <span className="min-w-0 flex-1 truncate">{action.label}</span>

                                      <span
                                        className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${typeConfig.badgeClassName}`}
                                      >
                                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[10px]" />
                                      </span>
                                    </button>
                                  )
                                })(),
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}

                {isLoading && (
                  <div className="flex w-full justify-start">
                    <p className="inline-flex max-w-[92%] rounded-2xl rounded-bl-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-500 shadow-sm sm:max-w-[82%]">
                      {t("loading")}
                    </p>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </section>

            <form
              onSubmit={handleSubmit}
              className="flex w-full shrink-0 items-center gap-2 border-t border-slate-200 p-2"
            >
              <div className="min-w-0 flex-1">
                <InputField
                  id="operational-help-question"
                  type="text"
                  value={question}
                  icon={faCircleQuestion}
                  placeholder={t("placeholder")}
                  onInvalidMessage={t("invalid")}
                  onChange={(event) => setQuestion(event.target.value)}
                  maxLength={1200}
                  descriptionKey="interactionDescriptions:components.operationalHelp.popup.question"
                />
              </div>

              <button
                type="button"
                aria-label={t("clear")}
                disabled={isLoading || messages.length <= 1}
                onClick={handleClearChat}
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-red-600 text-white shadow transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                data-interaction-description-key="interactionDescriptions:components.operationalHelp.popup.clear"
              >
                <FontAwesomeIcon icon={faEraser} />
              </button>

              <button
                type="submit"
                aria-label={t("ask")}
                disabled={isLoading || question.trim().length === 0}
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-orange-500 text-white shadow transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                data-interaction-description-key="interactionDescriptions:components.operationalHelp.popup.ask"
              >
                <FontAwesomeIcon icon={faPaperPlane} />
              </button>
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
